import React, { useCallback, useEffect } from 'react';

type MediaLightboxProps = {
  photos: string[];
  gameTitle: string;
  activeIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
};

function MediaLightbox({ photos, gameTitle, activeIndex, onClose, onNavigate }: MediaLightboxProps) {
  const goPrev = useCallback(() => {
    onNavigate((activeIndex - 1 + photos.length) % photos.length);
  }, [activeIndex, onNavigate, photos.length]);

  const goNext = useCallback(() => {
    onNavigate((activeIndex + 1) % photos.length);
  }, [activeIndex, onNavigate, photos.length]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'ArrowLeft') {
        goPrev();
      } else if (event.key === 'ArrowRight') {
        goNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goNext, goPrev, onClose]);

  return (
    <div
      className="kg-media-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`${gameTitle} media viewer`}
      onClick={onClose}
    >
      <button type="button" className="kg-media-lightbox-close" onClick={onClose} aria-label="Close">
        ×
      </button>

      <div className="kg-media-lightbox-stage" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="kg-media-lightbox-nav kg-media-lightbox-nav--prev" onClick={goPrev} aria-label="Previous image">
          ‹
        </button>

        <figure className="kg-media-lightbox-frame">
          <img
            src={photos[activeIndex]}
            alt={`${gameTitle} screenshot ${activeIndex + 1}`}
            className="kg-media-lightbox-img"
          />
        </figure>

        <button type="button" className="kg-media-lightbox-nav kg-media-lightbox-nav--next" onClick={goNext} aria-label="Next image">
          ›
        </button>
      </div>

      <div className="kg-media-lightbox-dots" onClick={(event) => event.stopPropagation()}>
        {photos.map((_, index) => (
          <button
            key={`dot-${index}`}
            type="button"
            className={`kg-media-lightbox-dot${index === activeIndex ? ' is-active' : ''}`}
            onClick={() => onNavigate(index)}
            aria-label={`Go to image ${index + 1}`}
            aria-current={index === activeIndex ? 'true' : undefined}
          />
        ))}
      </div>
    </div>
  );
}

export default MediaLightbox;
